import React, { useEffect, useState } from "react";
import {
    Box, Typography, Card, CardContent, Grid, Chip, 
    CircularProgress, Stack, Paper, Fade, Avatar,
    Divider, IconButton, Tooltip
} from "@mui/material";
import PublicIcon from '@mui/icons-material/Public';
import HubIcon from '@mui/icons-material/Hub';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebase";
import Menu from "../../panel/Menu";

function Otainfo() {
    const [otaData, setOtaData] = useState({ otas: [], roomMappings: [] });
    const [loading, setLoading] = useState(true);
    const [propertyId, setPropertyId] = useState("");

    useEffect(() => {
        const fetchId = async () => {
            const user = auth.currentUser;
            if (user) {
                const snap = await getDoc(doc(db, "authroles", user.uid));
                if (snap.exists()) setPropertyId(snap.data().propertyId);
            }
        };
        fetchId();
    }, []);

    useEffect(() => {
        const fetchOtaInfo = async () => {
            if (!propertyId) return;
            try {
                // 1. Fetch Room Inventory from Firestore for the names
                const inventorySnap = await getDoc(doc(db, "room_inventory", propertyId));
                let roomInventory = [];
                if (inventorySnap.exists()) {
                    roomInventory = inventorySnap.data().rooms || [];
                }

                

                // 2. Fetch Channel Manager credentials
                const configSnap = await getDoc(doc(db, "channel_manager", propertyId));
                if (configSnap.exists()) {
                    const { hotelCode, authCode } = configSnap.data();
                    
                    // 3. Fetch OTA Mapping from API
                    const res = await fetch('https://navajowhite-wren-495848.hostingersite.com/api/ota-info', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ HotelCode: hotelCode, AuthCode: authCode })
                    });
                    const data = await res.json();

                    if (data.APIResponse?.IsSuccess) {
                        
                        const enrichedMappings = (data.OTARooms || []).map(mapping => {
                            const matchedRoom = roomInventory.find(r => r.RoomId === mapping.RoomId);
                            return {
                                ...mapping,
                                RoomName: matchedRoom ? matchedRoom.RoomName : ""
                            };
                        });

                        setOtaData({
                            otas: data.OTAs || [],
                            roomMappings: enrichedMappings
                        });
                    }
                }
            } catch (err) {
                console.error("OTA Sync Error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchOtaInfo();
    }, [propertyId]);

    return (
        <Box sx={{ display: 'flex', bgcolor: "#F1F5F9", minHeight: "100vh" }}>
            <Menu />
            <Box className="content-wrapper" sx={{ flexGrow: 1, p: { xs: 1, md: 4 } }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 2 }} spacing={2}>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 900, color: "#0F172A", letterSpacing: '-0.02em' }}>
                            Channel Distribution
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#64748B", mt: 0.5 }}>
                            Mapped connections between your inventory and global booking channels.
                        </Typography>
                    </Box>
                    <Chip 
                        icon={<SyncAltIcon sx={{ fontSize: '16px !important' }} />}
                        label="Live Matrix Active" 
                        sx={{ px: 1, py: 2, fontWeight: 700, bgcolor: "#DCFCE7", color: "#166534", border: '1px solid #BBF7D0' }} 
                    />
                </Stack>

                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 15, gap: 2 }}>
                        <CircularProgress size={40} thickness={4} sx={{ color: '#6366F1' }} />
                        <Typography sx={{ color: '#64748B', fontWeight: 500 }}>Syncing Distribution Data...</Typography>
                    </Box>
                ) : (
                    <Grid className="row" container spacing={4}>
                        {/* Global Channels Summary */}
                        <Grid className="col-sm-12" item xs={12}>
                            <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: "1px solid #E2E8F0", bgcolor: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(10px)' }}>
                                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                                    <Avatar sx={{ bgcolor: '#EEF2FF', color: '#6366F1', width: 32, height: 32 }}>
                                        <PublicIcon fontSize="small" />
                                    </Avatar>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#475569", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Active Global Channels ({otaData.otas.length})
                                    </Typography>
                                </Stack>
                                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                    {otaData.otas.map((ota) => (
                                        <Chip key={ota.OTACode} label={ota.OTAName} variant="outlined" sx={{ bgcolor: '#FFF', fontWeight: 600, borderRadius: '8px' }} />
                                    ))}
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Room Mapping Matrix */}
                        <Grid className="col-sm-12" item xs={12}>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: "#334155", mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <HubIcon sx={{ color: '#6366F1' }} /> Distribution Matrix
                            </Typography>
                            
                            <Grid className="row" spacing={3}>
                                {otaData.roomMappings.map((mapping, index) => (
                                    <Grid className="col-sm-3 mb-2" item xs={12} md={6} lg={4} key={index}>
                                        <Fade in={true} timeout={300 + index * 100}>
                                            <Card sx={{ 
                                                borderRadius: 5, 
                                                border: "1px solid #E2E8F0",
                                                transition: 'transform 0.2s',
                                                '&:hover': { transform: 'translateY(-4px)', boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)" }
                                            }}>
                                                <CardContent sx={{ p: 3 }}>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                                                        <Box>
                                                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#6366F1', textTransform: 'uppercase' }}>
                                                                {mapping.RoomName} {/* Fetched from Firestore */}
                                                            </Typography>
                                                            <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                                                                <MeetingRoomIcon sx={{ color: '#64748B', fontSize: 18 }} />
                                                                <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                                                                    Room ID: {mapping.RoomId}
                                                                </Typography>
                                                            </Stack>
                                                        </Box>
                                                        <Chip label={`Rate Plan Id: ${mapping.RatePlanId}`} size="small" variant="filled" sx={{ fontWeight: 700, borderRadius: 1.5, bgcolor: '#F1F5F9' }} />
                                                    </Stack>

                                                    <Divider sx={{ my: 2, borderStyle: 'dashed' }} />

                                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', display: 'block', mb: 1.5 }}>
                                                        Connected Channels
                                                    </Typography>
                                                    
                                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                        {mapping.OTAs.map(ota => (
                                                            <Tooltip title={`Code: ${ota.OTACode}`} key={ota.OTACode} arrow>
                                                                <Chip 
                                                                    label={ota.OTAName} 
                                                                    size="small" 
                                                                    sx={{ 
                                                                        fontWeight: 700, 
                                                                        fontSize: '0.65rem', 
                                                                        bgcolor: '#F5F3FF', 
                                                                        color: '#7C3AED',
                                                                        border: '1px solid #DDD6FE'
                                                                    }} 
                                                                />
                                                            </Tooltip>
                                                        ))}
                                                        {mapping.OTAs.length === 0 && (
                                                            <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#94A3B8' }}>
                                                                No active channels mapped
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </CardContent>
                                                <Box sx={{ px: 3, py: 1.5, bgcolor: '#F8FAFC', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600 }}>
                                                        Status: <span style={{ color: '#10B981' }}>Active Sync</span>
                                                    </Typography>
                                                    <IconButton size="small">
                                                        <InfoOutlinedIcon fontSize="inherit" />
                                                    </IconButton>
                                                </Box>
                                            </Card>
                                        </Fade>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </Box>
        </Box>
    );
}

export default Otainfo;